import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Bell, Check, X, ArrowRight } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import {
  useNotificationCount,
  useNotifications,
  useDismissNotification,
  useResolveNotification,
  useRunNotificationAction,
  pendingBadgeLabel,
  RESOLVE_LABEL,
  type UserNotification,
} from '~/hooks/queries/use-notifications';
import { displayNotification } from '~/lib/notification-text';
import { NotificationBodyText } from '~/features/notifications/notification-body-text';
import { readBackgroundAuthItem } from '~/lib/background-auth-notification';
import { BackgroundAuthBody } from '~/features/notifications/background-auth-body';

const PREVIEW_LIMIT = 5;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const count = useNotificationCount();
  const list = useNotifications('pending');
  const pending = count.data?.pending ?? 0;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button
        onClick={() => setOpen((v) => !v)}
        variant="ghost"
        size="icon"
        aria-label={pending > 0 ? `Notifications, ${pending} need attention` : 'Notifications'}
        className={`text-muted-foreground hover:text-foreground h-8 w-8 relative ${
          pending > 0 ? 'text-foreground' : ''
        }`}
        tooltip={pending > 0 ? `${pending} item${pending === 1 ? '' : 's'} need attention` : 'Notifications'}
      >
        <Bell size={16} weight={pending > 0 ? 'fill' : 'regular'} />
        {pending > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-[length:var(--text-2xs)] font-semibold text-white flex items-center justify-center tabular-nums">
            {pendingBadgeLabel(pending)}
          </span>
        )}
      </Button>

      {open && (
        <NotificationPanel
          pending={pending}
          items={list.data ?? []}
          isLoading={list.isLoading}
          onClose={() => setOpen(false)}
          onShowAll={() => { setOpen(false); navigate('/notifications'); }}
        />
      )}
    </div>
  );
}

/**
 * The open popover. Exported so a test can render it: the popover only mounts
 * on a click, which a markup-string render cannot make. `pending` is the
 * server count (FRONT-2486) — the header and the "Show all" link both read it,
 * never `items.length`, which is capped at one page.
 */
export function NotificationPanel({
  pending,
  items,
  isLoading,
  onClose,
  onShowAll,
}: {
  pending: number;
  items: UserNotification[];
  isLoading: boolean;
  onClose: () => void;
  onShowAll: () => void;
}) {
  const visible = items.slice(0, PREVIEW_LIMIT);
  const overflow = Math.max(pending, items.length) - visible.length;
  return (
    <div className="absolute right-0 top-full mt-1.5 w-80 max-h-[28rem] overflow-y-auto rounded-md border border-border bg-popover shadow-lg z-50">
      <div className="px-3 py-2 border-b border-border flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground">Notifications</span>
        <span data-testid="notification-pending-count" className="text-[length:var(--text-2xs)] text-muted-foreground tabular-nums">
          {pending} pending
        </span>
      </div>
      {isLoading && (
        <div className="px-3 py-6 text-xs text-muted-foreground text-center">Loading…</div>
      )}
      {!isLoading && items.length === 0 && (
        <div className="px-3 py-6 text-xs text-muted-foreground text-center">
          You&rsquo;re all caught up.
        </div>
      )}
      {visible.map((n) => (
        <NotificationRow key={n.id} n={n} onNavigate={onClose} />
      ))}
      <button
        type="button"
        onClick={onShowAll}
        className="w-full px-3 py-2 border-t border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors flex items-center justify-center gap-1.5"
      >
        {overflow > 0 ? `Show all (${Math.max(pending, items.length)})` : 'Open notifications page'}
        <ArrowRight size={11} weight="bold" />
      </button>
    </div>
  );
}

function NotificationRow({ n, onNavigate }: { n: UserNotification; onNavigate: () => void }) {
  const run = useRunNotificationAction();
  const resolve = useResolveNotification();
  const dismiss = useDismissNotification();
  // FRONT-2363: a background job skipped on a dead login lists what was
  // skipped and carries its own actions.
  const backgroundAuth = readBackgroundAuthItem(n);
  const text = displayNotification(n);
  const tone =
    n.severity === 'error'
      ? 'border-l-destructive'
      : n.severity === 'warning'
        ? 'border-l-amber-500'
        : n.kind === 'autonomy_inform'
          ? 'border-l-chart-2'
          : 'border-l-primary';

  return (
    <div data-testid="notification-row" className={`px-3 py-2.5 border-b border-border last:border-b-0 border-l-2 ${tone}`}>
      <div className="flex items-start gap-2">
        {n.imageUrl && (
          <img
            src={n.imageUrl}
            alt="Final screenshot"
            loading="lazy"
            className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground">{text.title}</div>
          {text.body && (
            <NotificationBodyText
              text={text.body}
              onNavigate={onNavigate}
              className="text-xs text-muted-foreground mt-0.5 line-clamp-3"
            />
          )}
          {backgroundAuth ? (
            <BackgroundAuthBody n={n} item={backgroundAuth} onNavigate={onNavigate} />
          ) : (
            <div className="flex items-center gap-1.5 mt-2">
              {n.action ? (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => { run.mutate(n); onNavigate(); }}
                  disabled={run.isPending}
                >
                  <Check size={11} /> {n.action.label}
                </Button>
              ) : (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => resolve.mutate(n.id)}
                  disabled={resolve.isPending}
                  className="text-success hover:text-success"
                >
                  <Check size={11} /> {RESOLVE_LABEL}
                </Button>
              )}
              <Button
                size="xs"
                variant="ghost"
                onClick={() => dismiss.mutate(n.id)}
                disabled={dismiss.isPending}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={11} /> Dismiss
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
